import { ChangeDetectorRef, Component, OnInit, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { ProductAttributes, ProductBody, ProductCategories } from "@models/product.model";
import { DialogSaveDataModel, OrderAttributes, RefundOption } from "src/models/order.model";
import { RatingAndReviewComponent } from "./rating-and-review/rating-and-review.component";
import { OrderStatus, status } from "../../models/order.model";
import { OrderService } from "../services/order/order.service";
import { AdminStatusHistoryModel } from "@models/adminStatusHistory.model";
import { UserService } from "../services/user/user.service";
import { environment } from "src/environments/environment";
import { PaymentMethod, PaymentStatus, RefundStatus } from "@models/payment.model";
import { GenericDialogComponent } from "../shared/generic-dialog/generic-dialog.component";
import { MatSnackBar } from "@angular/material/snack-bar";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { finalize, Observable, switchMap, take, tap } from "rxjs";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { StructureService } from "../services/structure/structure.service";
import { ProductService } from "../services/product/product.service";
import { DialogService } from "../services/dialog/dialog.service";
import { TranslateService } from "@ngx-translate/core";
import { OrderItemAttributes } from "@models/orderItem.model";
import { BaseReview } from "@models/review.model";

@Component({
    selector: "app-order-details",
    templateUrl: "./order-details.component.html",
    styleUrls: ["./order-details.component.scss"],
    standalone: false,
})
export class OrderDetailsComponent implements OnInit {
    public order!: OrderAttributes;
    public noDataFound: boolean = false;
    public filteredStatusHistory?: AdminStatusHistoryModel[] = [];
    public showTimeline!: boolean;
    public currency = environment.currency.indianRupee;
    public totalProducts: number | undefined;
    public subtotal: number | undefined;
    readonly dialog = inject(MatDialog);
    public orderStatusHistory: AdminStatusHistoryModel[] = [];
    public orderStatusTrack!: status[];
    public showTrack!: boolean;
    public getUserId!: number;
    public OrderStatus = OrderStatus;
    public currentOrder: OrderAttributes | undefined;
    public currentOrderItem!: OrderItemAttributes;
    public returnReason!: string;
    public exchangeReason!: string;
    public showReturnPopup = false;
    public orderID!: string;
    public selectedItemIds: number[] = [];
    public nonCancelledItems: OrderItemAttributes[] = [];
    public hasCancelledItems: boolean = false;
    public hasReturnedItems: boolean = false;
    public hasExchangeItems: boolean = false;
    public refundStatus = RefundStatus;
    public isAllRefundSuccessful: boolean = false;
    public isSelectAllChecked = false;
    public loading: boolean = true;
    public recentDeliveredOrExchangedItemIds!: number[];
    public PaymentMethod = PaymentMethod;

    public selectableIds: Set<number> = new Set();

    public cancellableItems: OrderItemAttributes[] = [];
    public cancelledItems: OrderItemAttributes[] = [];
    public returnableItems: OrderItemAttributes[] = [];
    public returnedItems: OrderItemAttributes[] = [];
    public exchangeableItems: OrderItemAttributes[] = [];
    public exchangeItems: OrderItemAttributes[] = [];

    public cancellableStatuses = [OrderStatus.Ordered, OrderStatus.Packed];
    public returnableStatuses = [OrderStatus.Delivered, OrderStatus.Exchanged];
    public exchangeableStatuses = [OrderStatus.Delivered];

    public cancellingStatuses = [OrderStatus.Cancelled];
    public returningStatuses = [OrderStatus.ReturnInitiated, OrderStatus.PickupScheduled, OrderStatus.Returned];
    public exchangingStatuses = [
        OrderStatus.ExchangeInitiated,
        OrderStatus.ExchangePacked,
        OrderStatus.ExchangeDispatched,
        OrderStatus.Exchanged,
    ];

    constructor(
        private orderService: OrderService,
        private route: ActivatedRoute,
        private userService: UserService,
        private router: Router,
        private cdr: ChangeDetectorRef,
        private snackBar: MatSnackBar,
        private structureService: StructureService,
        private productService: ProductService,
        private dialogService: DialogService,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        this.init();
    }

    public init(): void {
        this.loading = true;
        this.orderID = this.route.snapshot.params["id"];

        this.getRecentDeliveredOrExchangedItemIds()
            .pipe(
                switchMap(() => this.orderService.getOrderById(this.orderID)),
                finalize(() => {
                    this.loading = false;
                    this.cdr.detectChanges();
                }),
            )
            .subscribe({
                next: (response) => {
                    if (!response) {
                        this.noDataFound = true;
                        return;
                    }

                    this.order = response;
                    this.setupOrderItemData();
                    this.cdr.detectChanges();
                },
                error: (error) => {
                    throw error;
                },
            });

        this.userService.getUserDetails().subscribe((response) => {
            if (response) {
                this.getUserId = response.id;
            }
        });
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    private setupOrderItemData(): void {
        this.cancellableItems = this.order.orderItems!.filter(
            (item) =>
                this.cancellableStatuses.includes(item.itemStatus) &&
                !this.hasStatus(this.order.orderItems!, this.cancellingStatuses),
        );

        this.returnableItems = this.order.orderItems!.filter(
            (item) =>
                this.returnableStatuses.includes(item.itemStatus) &&
                this.isRecentDeliveredOrExchangedItem(item) &&
                !this.hasStatus(this.order.orderItems!, this.returningStatuses),
        );

        this.exchangeableItems = this.order.orderItems!.filter(
            (item) =>
                this.exchangeableStatuses.includes(item.itemStatus) &&
                this.isRecentDeliveredOrExchangedItem(item) &&
                !this.hasStatusInItemStatusHistory(this.order.orderItems!, this.exchangingStatuses),
        );

        this.selectableIds = this.getSelectableIds();

        this.cancelledItems = this.order.orderItems!.filter((item) => item.itemStatus === OrderStatus.Cancelled);

        this.nonCancelledItems = this.order.orderItems!.filter(
            (item) =>
                ![
                    OrderStatus.Cancelled,
                    OrderStatus.ReturnInitiated,
                    OrderStatus.PickupScheduled,
                    OrderStatus.Returned,
                    OrderStatus.ExchangeInitiated,
                    OrderStatus.ExchangePacked,
                    OrderStatus.ExchangeDispatched,
                    OrderStatus.Exchanged,
                ].includes(item.itemStatus),
        );

        this.returnedItems = this.order.orderItems!.filter((item) =>
            [OrderStatus.ReturnInitiated, OrderStatus.PickupScheduled, OrderStatus.Returned].includes(item.itemStatus),
        );

        this.exchangeItems = this.order.orderItems!.filter((item) =>
            [
                OrderStatus.ExchangeInitiated,
                OrderStatus.ExchangePacked,
                OrderStatus.ExchangeDispatched,
                OrderStatus.Exchanged,
            ].includes(item.itemStatus),
        );

        this.isAllRefundSuccessful = this.order.orderItems!.every(
            (item) => item.refund !== null && item.refund?.refundStatus === RefundStatus.RefundSuccessful,
        );

        this.filteredStatusHistory =
            this.order.orderStatusHistory!.filter(
                (history) => history.status !== OrderStatus.Pending && history.status !== OrderStatus.Failed,
            ) || [];

        this.showTimeline = this.filteredStatusHistory.length > 0;
    }

    get refundStatusMessage(): string {
        return this.order?.orderItems?.every(
            (item) => item.refund !== null && item.refund?.refundStatus === RefundStatus.RefundSuccessful,
        )
            ? RefundStatus.RefundSuccessful
            : "";
    }

    get isOrderFullyCancelledOrReturned(): boolean | undefined {
        return this.order?.orderItems?.every(
            (item) => item.itemStatus === OrderStatus.Cancelled || item.itemStatus === OrderStatus.Returned,
        );
    }

    public getOrderStatusClasses(item?: OrderItemAttributes): { [key: string]: boolean } {
        return {
            failed: this.order.orderStatus === OrderStatus.Failed,
            pending: this.order.orderStatus === OrderStatus.Pending,
            ordered: this.order.orderStatus === OrderStatus.Ordered,
            packed: this.order.orderStatus === OrderStatus.Packed,
            cancelled: this.order.orderStatus === OrderStatus.Cancelled,
            dispatched: this.order.orderStatus === OrderStatus.Dispatched,
            delivered: this.order.orderStatus === OrderStatus.Delivered,
            returnInitiated:
                this.order.orderStatus === OrderStatus.ReturnInitiated ||
                item?.itemStatus === OrderStatus.ReturnInitiated,
            pickupScheduled:
                this.order.orderStatus === OrderStatus.PickupScheduled ||
                item?.itemStatus === OrderStatus.PickupScheduled,
            returned: this.order.orderStatus === OrderStatus.Returned || item?.itemStatus === OrderStatus.Returned,
            exchangeInitiated:
                this.order.orderStatus === OrderStatus.ExchangeInitiated ||
                item?.itemStatus === OrderStatus.ExchangeInitiated,
            exchangePacked:
                this.order.orderStatus === OrderStatus.ExchangePacked ||
                item?.itemStatus === OrderStatus.ExchangePacked,
            exchangeDispatched:
                this.order.orderStatus === OrderStatus.ExchangeDispatched ||
                item?.itemStatus === OrderStatus.ExchangeDispatched,
            exchanged: this.order.orderStatus === OrderStatus.Exchanged || item?.itemStatus === OrderStatus.Exchanged,
        };
    }

    // Example method to dynamically set classes based on payment status
    public getPaymentStatusClasses(): { [key: string]: boolean } {
        return {
            pending: this.order.paymentStatus === PaymentStatus.Pending,
            success: this.order.paymentStatus === PaymentStatus.Success,
            failed: this.order.paymentStatus === PaymentStatus.Failed,
        };
    }

    public getRefundStatusClass(refundStatus: string | undefined): string {
        return this.orderService.getRefundStatusClass(refundStatus);
    }

    public isOverflow(el: HTMLElement): boolean {
        return el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight;
    }

    public isReviewDisabled(product: ProductAttributes | undefined): boolean {
        if (!this.order || !this.order.reviews) {
            return true;
        }

        return this.order.reviews.some(
            (review: BaseReview) => review.productId === product?.id && review.userId === this.getUserId,
        );
    }

    public openRatingPopup(product: ProductBody | undefined, order: OrderAttributes): void {
        const dialogRef = this.dialog.open(RatingAndReviewComponent, {
            disableClose: true,
            width: "450px",
            data: {
                product_details: product,
                order_details: order,
                userId: this.getUserId,
            },
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (result === true) {
                setTimeout(() => {
                    this.init();
                }, 1000);
            }
        });
    }

    public toggleItemSelection(itemId: number, isChecked: boolean): void {
        if (isChecked) {
            // Add product ID to the list
            this.selectedItemIds.push(itemId);
        } else {
            // Remove product ID from the list
            this.selectedItemIds = this.selectedItemIds.filter((id) => id !== itemId);
        }
    }

    public isSelectAllVisible(): boolean {
        const recentExchanged = this.exchangeItems.filter((item) => this.isRecentDeliveredOrExchangedItem(item)).length;
        return this.nonCancelledItems.length + recentExchanged > 1;
    }

    public isSelected(itemId: number): boolean {
        return this.selectedItemIds.includes(itemId);
    }

    public isAllSelected(): boolean {
        return this.selectedItemIds.length === this.selectableIds.size;
    }

    public isSomeSelected(): boolean {
        return this.selectedItemIds.length > 0 && !this.isAllSelected();
    }

    // Toggle select/deselect all items
    public toggleSelectAll(event: MatCheckboxChange): void {
        this.isSelectAllChecked = event.checked;

        if (this.isSelectAllChecked) {
            this.selectedItemIds = Array.from(this.selectableIds);
        } else {
            this.selectedItemIds = [];
        }
    }

    private openDialog(): void {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.CancelOrder,
                title: this.getTranslation("genericDialogBox.cancelOrder.title"),
                content:
                    this.order.paymentMethod === PaymentMethod.CashOnDelivery
                        ? this.getTranslation("genericDialogBox.cancelOrder.codContent")
                        : this.getTranslation("genericDialogBox.cancelOrder.onlineContent"),
                firstBtn: this.getTranslation("genericDialogBox.button.okBtn"),
                secondBtn: this.getTranslation("genericDialogBox.button.cancelBtn"),
            },
            autoFocus: false,
            restoreFocus: false,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.confirmCancelSelectedItem();
            }
        });
    }

    private confirmCancelSelectedItem(): void {
        // this.orderService.cancelSelectedItem(this.orderID, this.selectedItemIds).subscribe((response) => {
        //     if (response) {
        //         this.isSelectAllChecked = false;
        //         this.selectedItemIds = [];
        //         this.init();
        //         this.snackBar.open(response.message, SnackBarConfig.Actions.CLOSE, {
        //             duration: SnackBarConfig.Duration.MEDIUM,
        //         });
        //     }
        // });
    }

    public cancelOrReturn(order: OrderAttributes): void {
        if (order.orderStatus === OrderStatus.Delivered) {
            if (order.paymentMethod === PaymentMethod.CashOnDelivery) {
                this.openDialogReturnCoD();
            } else {
                this.openDialogReturn();
            }
        } else if (order.orderStatus === OrderStatus.Ordered || order.orderStatus === OrderStatus.Packed) {
            this.openDialog();
        }
    }

    public openDialogReturn(): void {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.ReturnOrder,
                title: this.getTranslation("genericDialogBox.returnOrder.title"),
                content: this.getTranslation("genericDialogBox.returnOrder.content"),
                inputs: [
                    {
                        controlName: "reason",
                        value: "",
                        placeholder: this.getTranslation("genericDialogBox.returnOrder.placeHolder.return"),
                        type: "text",
                    },
                ],
                firstBtn: this.getTranslation("genericDialogBox.button.returnBtn"),
                secondBtn: this.getTranslation("genericDialogBox.button.cancelBtn"),
            },
            autoFocus: false,
            restoreFocus: false,
        });
        dialogRef.componentInstance.saveClicked.subscribe((savedData: DialogSaveDataModel) => {
            if (savedData) {
                this.confirmReturnSelectedItem(savedData);
            }
        });
    }

    public openDialogReturnCoD(): void {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.ReturnOrderCod,
                title: this.getTranslation("genericDialogBox.onlineReturnOrder.title"),
                content: this.getTranslation("genericDialogBox.onlineReturnOrder.content"),
                content2: this.getTranslation("genericDialogBox.onlineReturnOrder.content2"),
                firstBtn: this.getTranslation("genericDialogBox.button.returnBtn"),
                secondBtn: this.getTranslation("genericDialogBox.button.cancelBtn"),
            },
            autoFocus: false,
            restoreFocus: false,
        });
        dialogRef.componentInstance.saveClicked.subscribe((savedData: DialogSaveDataModel) => {
            if (savedData) {
                this.confirmReturnSelectedItem(savedData);
            }
        });
    }

    private confirmReturnSelectedItem(data: DialogSaveDataModel): void {
        this.returnReason = data.reason;

        const refundData = {
            refundOption: data.refundOption,
            ...(data.refundOption === RefundOption.BankDetails && {
                accountHolderName: data.bankDetails?.accountHolderName,
                accountNumber: data.bankDetails?.accountNumber,
                ifscCode: data.bankDetails?.ifscCode,
            }),
            ...(data.refundOption === RefundOption.Upi && {
                upiId: data.upi?.upiId,
            }),
        };

        // this.orderService
        //     .returnSelectedItem(
        //         this.orderID,
        //         this.selectedItemIds,
        //         this.returnReason,
        //         refundData.refundOption,
        //         refundData.accountHolderName,
        //         refundData.accountNumber,
        //         refundData.ifscCode,
        //         refundData.upiId,
        //     )
        //     .subscribe((response) => {
        //         if (response) {
        //             this.isSelectAllChecked = false;
        //             this.selectedItemIds = [];
        //             this.init();
        //             this.snackBar.open(response.message, SnackBarConfig.Actions.CLOSE, {
        //                 duration: SnackBarConfig.Duration.MEDIUM,
        //             });
        //         }
        //     });
    }

    public openExchangeDialog(order: OrderAttributes): void {
        this.currentOrder = order;
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: {
                componentName: DialogInvokingComponents.ExchangeMessage,
                title: this.getTranslation("genericDialogBox.exchangeOrder.title"),
                content: this.getTranslation("genericDialogBox.exchangeOrder.content"),
                inputs: [
                    {
                        label: this.getTranslation("genericDialogBox.help.label.message"),
                        placeholder: this.getTranslation("genericDialogBox.help.placeHolder.message"),
                        controlName: "exchangeQuery",
                        type: "text",
                    },
                ],
                firstBtn: this.getTranslation("genericDialogBox.button.exchangeBtn"),
                secondBtn: this.getTranslation("genericDialogBox.button.cancelBtn"),
            },
            autoFocus: false,
            restoreFocus: false,
        });

        dialogRef.componentInstance.saveClicked.subscribe((savedData: any) => {
            if (savedData) {
                this.confirmExchangeSelectedItem(order, savedData.exchangeQuery);
            }
        });
    }

    public confirmExchangeSelectedItem(order: OrderAttributes, reason: string): void {
        this.exchangeReason = reason;
        // this.orderService.exchangeSelectedItem(this.orderID, this.selectedItemIds, this.exchangeReason).subscribe({
        //     next: (response) => {
        //         this.isSelectAllChecked = false;
        //         this.selectedItemIds = [];
        //         this.init();
        //         this.snackBar.open(response.message, SnackBarConfig.Actions.CLOSE, {
        //             duration: SnackBarConfig.Duration.MEDIUM,
        //         });
        //     },
        //     error: (error) => {
        //         throw error;
        //     },
        // });
    }

    private hasStatus(items: OrderItemAttributes[], statuses: OrderStatus[]): boolean {
        return items.some((item) => statuses.includes(item.itemStatus));
    }

    private hasStatusInItemStatusHistory(items: OrderItemAttributes[], statuses: OrderStatus[]): boolean {
        return items.some((item) => item.itemStatusHistory?.some((history) => statuses.includes(history.status)));
    }

    public getSelectableIds(): Set<number> {
        const selectableIds = new Set<number>([
            ...this.cancellableItems.map((item) => item.id!),
            ...this.returnableItems.map((item) => item.id!),
            ...this.exchangeableItems.map((item) => item.id!),
        ]);

        return selectableIds;
    }

    public isItemSelectable(itemId: number): boolean {
        return this.selectableIds.has(itemId);
    }

    public getRecentDeliveredOrExchangedItemIds(): Observable<number[]> {
        return this.orderService.getRecentDeliveredOrExchangedItemIds().pipe(
            tap((response) => {
                this.recentDeliveredOrExchangedItemIds = response;
            }),
        );
    }

    public isRecentDeliveredOrExchangedItem(item: OrderItemAttributes): boolean {
        return this.recentDeliveredOrExchangedItemIds?.includes(item.id!);
    }

    public isAnyItemRecentlyDeliveredOrExchanged(items: OrderItemAttributes[]): boolean {
        return items.some((item) => this.isRecentDeliveredOrExchangedItem(item));
    }

    public showReviewBtn(item: OrderItemAttributes): boolean {
        return (
            item.itemStatus === OrderStatus.Delivered ||
            item.itemStatus === OrderStatus.ReturnInitiated ||
            item.itemStatus === OrderStatus.PickupScheduled ||
            item.itemStatus === OrderStatus.Returned
        );
    }

    public goToStructureFromOrder(productId: number): void {
        if (!productId) {
            return;
        }
        this.structureService
            .getStructureByProductId(productId)
            .pipe(take(1))
            .subscribe((response) => {
                if (response?.id) {
                    this.router.navigate([`/designer/${response.id}`], { queryParams: { fromProductDetail: true } });
                }
            });
    }

    public getCountInfo(modelUrl?: string): void {
        if (modelUrl) {
            this.structureService.fetchStructureData(modelUrl).subscribe((res) => {
                if (res) {
                    const shapeCountObject = this.productService.calculateShapeInfo(res);
                    const dialogData = {
                        componentName: DialogInvokingComponents.ProductDetailPage,
                        title: this.getTranslation("genericDialogBox.viewCount.title"),
                        options: shapeCountObject.filteredShapeArray,
                        value: shapeCountObject.totalBlock,
                    };
                    this.dialogService.openDialog(dialogData, "50%", { disableClose: true });
                }
            });
        }
    }

    public showStructureLink(product: ProductAttributes): boolean {
        return !product?.category?.includes(ProductCategories.BuyBlocks);
    }
}
